import type { IUpdateSchoolProfilePayload } from '@/types/payload';

import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { IconEdit, IconCamera, IconLoader2 } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import CustomImage from '@/components/CustomImage';
import Input from '@/components/Input/Input';
import PhoneNumberInput from '@/components/phoneInput';
import TeacherProfileSkeleton from '@/components/SkeletonLoader/TeacherProfileSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import {
  teacherProfileSchema,
  type TTeacherProfileForm,
} from '@/schema/teacherProfileSchema';
import {
  updateTeacherProfile,
  uploadUserProfileImage,
} from '@/service/settingsService';
import { getTeacherProfile } from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import useUser from '@/store/useUser';
import { ITeacherProfileData } from '@/types/profile';
import { getAssetUrl } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-2">
    <span className="text-sm text-neutral-secondary font-medium">{label}</span>
    <span className="text-base font-semibold text-neutral-primary">
      {value}
    </span>
  </div>
);

const ProfileSettings = () => {
  const { formatText } = useFormatText();
  const { user, setUser } = useUser();
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();
  const [profile, setProfile] = useState<ITeacherProfileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedAssetId, setUploadedAssetId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TTeacherProfileForm>({
    resolver: zodResolver(teacherProfileSchema),
    mode: 'onChange',
  });

  const selectedCountryCode = watch('countryCode');

  const mapProfileToForm = useCallback(
    (data: ITeacherProfileData): TTeacherProfileForm => ({
      name: data.name || '',
      phone: data.phone || '',
      countryCode: data.phone_code || '+91',
    }),
    []
  );

  const fetchProfile = useCallback(async () => {
    setScreenLoading(true);
    try {
      const response = await getTeacherProfile();
      if (response?.status) {
        const data = response.data as ITeacherProfileData;
        setProfile(data);
        reset(mapProfileToForm(data));
        setProfileImage(getAssetUrl(data?.asset) || null);
        setUploadedAssetId(data.asset?.id || null);
      }
    } catch (error) {
      console.error('Error fetching teacher profile:', error);
    } finally {
      setScreenLoading(false);
    }
  }, [mapProfileToForm, reset, setScreenLoading]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleEdit = useCallback(() => {
    if (profile) reset(mapProfileToForm(profile));
    setIsEditing(true);
  }, [mapProfileToForm, profile, reset]);

  const handleCancel = useCallback(() => {
    if (profile) reset(mapProfileToForm(profile));
    setProfileImage(getAssetUrl(profile?.asset) || null);
    setUploadedAssetId(profile?.asset?.id || null);
    setIsEditing(false);
  }, [mapProfileToForm, profile, reset]);

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please upload an image file', { variant: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('Image size should be less than 5MB', {
          variant: 'error',
        });
        return;
      }

      // Preview
      const reader = new FileReader();
      reader.onloadend = () => setProfileImage(reader.result as string);
      reader.readAsDataURL(file);

      // Upload
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'teacher-profile');
        const res = await uploadUserProfileImage(formData);
        if (res?.status && res.data?.id) {
          setUploadedAssetId(res.data.id);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  const onSubmit = useCallback(
    async (data: TTeacherProfileForm) => {
      try {
        const payload: IUpdateSchoolProfilePayload = {
          name: data.name,
          phone_code: data.countryCode || '+91',
          phone_number: data.phone,
          asset_id: uploadedAssetId || profile?.asset?.id || '',
        };

        const response = await updateTeacherProfile(payload);

        if (response?.status) {
          setUser({
            ...user,
            name: payload.name,
            profile_pic: profileImage || getAssetUrl(user?.profile_pic),
          });
          enqueueSnackbar(response.message, { variant: 'success' });
          setIsEditing(false);
          await fetchProfile();
        } else {
          console.error('Failed to update profile:', response?.message);
        }
      } catch (error) {
        console.error(error);
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [profile?.asset?.id, uploadedAssetId, user, profileImage]
  );

  if (isScreenLoading) {
    return <TeacherProfileSkeleton />;
  }

  const displayName = profile?.name || user.name;
  const displayEmail = profile?.email || user.email;
  const displayPhone = profile?.phone
    ? `${profile.phone_code} ${profile.phone}`
    : '-';

  return (
    <div className="w-full max-w-216 flex flex-col gap-6">
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.profile.title')}
      </h2>

      <div className="border border-neutral-light-200 rounded-xl bg-white">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-neutral-primary">
                {formatText('settings.profile.profile-information')}
              </h3>

              <div className="flex items-center gap-3">
                {isEditing ? (
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      label={formatText('common.actions.cancel')}
                      onClick={handleCancel}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      label={formatText('common.actions.save-changes')}
                      disabled={isSubmitting || isUploading}
                    />
                  </div>
                ) : (
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      !isEditing
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-1 pointer-events-none absolute'
                    }`}
                  >
                    <Button
                      variant="outline"
                      onClick={handleEdit}
                      className=" rounded-lg hover:bg-neutral-light-50 transition-colors cursor-pointer"
                      icon={
                        <IconEdit
                          size={20}
                          className="text-neutral-secondary"
                        />
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Avatar + fields */}
            <div className="pb-6 border-b border-neutral-light-200 flex items-center gap-10">
              {/* Avatar */}
              <div className="relative w-25 h-25 shrink-0 group">
                {profileImage ? (
                  <CustomImage
                    src={profileImage}
                    alt={displayName}
                    className="w-25! h-25! min-w-25! rounded-full object-cover"
                  />
                ) : (
                  <UserAvatarList
                    user={displayName}
                    className="w-25! h-25! min-w-25! text-3xl!"
                  />
                )}

                {/* Loader */}
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                    <IconLoader2
                      size={28}
                      className="text-white animate-spin"
                    />
                  </div>
                )}

                {isEditing && !isUploading && (
                  <>
                    <Button
                      variant="unstyled"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      icon={<IconCamera size={24} className="text-white" />}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpg,image/jpeg"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </>
                )}
              </div>

              {/* Fields grid */}
              <div
                className={`flex-1 grid grid-cols-3 ${isEditing ? 'gap-4' : 'gap-10'}`}
              >
                {/* Full Name */}
                {isEditing ? (
                  <Input
                    name="name"
                    label={formatText('settings.profile.full-name')}
                    register={register}
                    error={
                      errors.name?.message
                        ? formatText(errors.name.message)
                        : undefined
                    }
                    required
                    autoFocus
                  />
                ) : (
                  <ReadOnlyField
                    label={formatText('settings.profile.full-name')}
                    value={displayName}
                  />
                )}

                {/* Email (always read-only) */}
                <ReadOnlyField
                  label={formatText('settings.profile.email-id')}
                  value={displayEmail}
                />

                {/* Phone */}
                {isEditing ? (
                  <PhoneNumberInput
                    name="phone"
                    label={formatText('settings.profile.phone-number')}
                    register={register}
                    setValue={setValue}
                    countryFlag
                    error={
                      errors.phone?.message
                        ? formatText(errors.phone.message)
                        : undefined
                    }
                    countrySelected={selectedCountryCode || '+91'}
                    required
                  />
                ) : (
                  <ReadOnlyField
                    label={formatText('settings.profile.phone-number')}
                    value={displayPhone}
                  />
                )}
              </div>
            </div>

            {/* All Classes */}
            <div className="flex flex-col gap-4">
              <h3 className="text-base font-semibold text-neutral-primary">
                {formatText('settings.profile.all-classes')}
              </h3>

              {profile?.all_classes?.length === 0 ? (
                <p className="text-lg font-normal text-neutral-primary text-center py-10">
                  {formatText('common.no-data-found')}
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  {profile?.all_classes?.map((cls) => (
                    <div
                      key={cls.id}
                      className="flex gap-4 p-4 py-2 items-center bg-white border border-neutral-light-200 rounded-lg"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-neutral-light-200 rounded-full text-neutral-primary text-base font-semibold uppercase shrink-0">
                        {cls.name.substring(0, 2)}
                      </div>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-normal text-neutral-primary leading-5 truncate">
                          {cls.name}
                        </span>
                        <span className="text-sm font-normal text-neutral-secondary leading-5">
                          {cls.total_students}{' '}
                          {formatText('settings.profile.students')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings;
